import { parseParticleSheet } from "./pcf.js";
import { Binary } from "./binary.js";
import { logger } from "./log.js";
const log = logger("Texture");
const sizes = {
    0: 4,
    1: 4,
    2: 3,
    3: 3,
    4: 2,
    5: 1,
    6: 2,
    8: 1,
    11: 4,
    12: 4,
    16: 4,
    19: 2,
    20: 2,
    22: 2,
    23: 4,
    24: 8,
    25: 8,
    26: 4,
    27: 4,
    28: 12,
    29: 16,
};
const compressed = (f) => [13, 14, 15, 20].includes(f);
const length = (w, h, f) =>
    compressed(f)
        ? Math.ceil(w / 4) * Math.ceil(h / 4) * (f === 13 || f === 20 ? 8 : 16)
        : w *
          h *
          (sizes[f] ??
              (() => {
                  throw new Error(`Unsupported VTF format ${f}`);
              })());
function color565(v) {
    return [
        ((v >> 11) * 255) / 31,
        (((v >> 5) & 63) * 255) / 63,
        ((v & 31) * 255) / 31,
        255,
    ];
}
export function decodeDxt(data, width, height, format) {
    const r = new Binary(data),
        output = new Uint8Array(width * height * 4);
    let offset = 0;
    for (let y = 0; y < height; y += 4)
        for (let x = 0; x < width; x += 4) {
            const dxt1 = format === 13 || format === 20,
                colorOffset = offset + (dxt1 ? 0 : 8);
            const c0 = r.u16(colorOffset),
                c1 = r.u16(colorOffset + 2),
                colors = [color565(c0), color565(c1)];
            if (c0 > c1 || !dxt1) {
                colors.push(
                    colors[0].map((v, i) =>
                        i === 3 ? 255 : (2 * v + colors[1][i]) / 3,
                    ),
                    colors[0].map((v, i) =>
                        i === 3 ? 255 : (v + 2 * colors[1][i]) / 3,
                    ),
                );
            } else
                colors.push(
                    colors[0].map((v, i) =>
                        i === 3 ? 255 : (v + colors[1][i]) / 2,
                    ),
                    [0, 0, 0, 0],
                );
            const selectors = r.u32(colorOffset + 4),
                alphas = [r.u8(offset), r.u8(offset + 1)];
            if (format === 15) {
                if (alphas[0] > alphas[1])
                    for (let i = 1; i <= 6; i++)
                        alphas.push(((7 - i) * alphas[0] + i * alphas[1]) / 7);
                else {
                    for (let i = 1; i <= 4; i++)
                        alphas.push(((5 - i) * alphas[0] + i * alphas[1]) / 5);
                    alphas.push(0, 255);
                }
            }
            let alphaBits = 0n;
            if (format === 15)
                for (let i = 0; i < 6; i++)
                    alphaBits |= BigInt(r.u8(offset + 2 + i)) << BigInt(i * 8);
            for (let i = 0; i < 16; i++) {
                const px = x + (i % 4),
                    py = y + (i >> 2);
                if (px >= width || py >= height) continue;
                const color = [...colors[(selectors >>> (i * 2)) & 3]];
                if (format === 14)
                    color[3] =
                        ((r.u8(offset + (i >> 1)) >> ((i & 1) * 4)) & 15) * 17;
                if (format === 15)
                    color[3] =
                        alphas[Number((alphaBits >> BigInt(i * 3)) & 7n)];
                output.set(color, (py * width + px) * 4);
            }
            offset += dxt1 ? 8 : 16;
        }
    return output;
}
function half(v) {
    const s = v & 32768 ? -1 : 1,
        e = (v >> 10) & 31,
        m = v & 1023;
    return e === 0
        ? (s * 2 ** -14 * m) / 1024
        : e === 31
          ? 0
          : s * 2 ** (e - 15) * (1 + m / 1024);
}
function pixels(bytes, w, h, format) {
    if (compressed(format)) return decodeDxt(bytes, w, h, format);
    const r = new Binary(bytes),
        floating = [24, 25, 27, 28, 29].includes(format),
        out = floating
            ? new Float32Array(w * h * 4)
            : new Uint8Array(w * h * 4),
        stride = sizes[format];
    for (let i = 0; i < w * h; i++) {
        const o = i * stride;
        let c;
        switch (format) {
            case 0:
                c = [...r.slice(o, 4)];
                break;
            case 1:
                c = [r.u8(o + 3), r.u8(o + 2), r.u8(o + 1), r.u8(o)];
                break;
            case 2:
                c = [...r.slice(o, 3), 255];
                break;
            case 3:
                c = [r.u8(o + 2), r.u8(o + 1), r.u8(o), 255];
                break;
            case 4:
                c = color565(r.u16(o));
                break;
            case 5:
                c = [r.u8(o), r.u8(o), r.u8(o), 255];
                break;
            case 6:
                c = [r.u8(o), r.u8(o), r.u8(o), r.u8(o + 1)];
                break;
            case 8:
                c = [255, 255, 255, r.u8(o)];
                break;
            case 11:
                c = [r.u8(o + 1), r.u8(o + 2), r.u8(o + 3), r.u8(o)];
                break;
            case 12:
            case 16:
                c = [
                    r.u8(o + 2),
                    r.u8(o + 1),
                    r.u8(o),
                    format === 12 ? r.u8(o + 3) : 255,
                ];
                break;
            case 24:
                c = Array.from({ length: 4 }, (_, j) => half(r.u16(o + j * 2)));
                break;
            case 22:
            case 23:
            case 26: {
                const signed = j => Math.round((Math.max(-1, r.i8(o + j) / 127) * 0.5 + 0.5) * 255);
                c = [signed(0), signed(1), format === 22 ? 255 : format === 23 ? signed(2) : r.u8(o + 2),
                    format === 23 ? signed(3) : 255];
                break;
            }
            case 25:
                c = Array.from(
                    { length: 4 },
                    (_, j) => r.u16(o + j * 2) / 65535,
                );
                break;
            case 27:
                c = [r.f32(o), 0, 0, 1];
                break;
            case 28:
                c = [...r.vec(o), 1];
                break;
            case 29:
                c = [...r.vec(o), r.f32(o + 12)];
                break;
            default:
                throw new Error(`Unsupported VTF format ${format}`);
        }
        out.set(c, i * 4);
    }
    return out;
}
export function parseVtf(bytes, useS3tc = false, animation = false) {
    const started = performance.now();
    log.debug("parse start", { bytes: bytes.byteLength, useS3tc });
    const r = new Binary(bytes);
    if (r.text(0, 4) !== "VTF\0" || r.u32(4) !== 7 || r.u32(8) > 5)
        throw new Error("Unsupported VTF version");
    const minor = r.u32(8),
        width = r.u16(16),
        height = r.u16(18),
        flags = r.u32(20),
        frames = Math.max(1, r.u16(24)),
        format = r.u32(52),
        mips = r.u8(56);
    if (
        !width ||
        !height ||
        width > 16384 ||
        height > 16384 ||
        !mips ||
        mips > 15 ||
        frames > 1024
    )
        throw new Error("Invalid VTF dimensions");
    if (minor >= 2 && r.u16(63) > 1)
        throw new Error("Volume textures are unsupported");
    const faces =
        flags & 0x4000 ? (minor < 5 && r.u16(26) !== 0xffff ? 7 : 6) : 1;
    const lowFormat = r.u32(57),
        lowW = r.u8(61),
        lowH = r.u8(62);
    let offset =
        r.u32(12) +
        (lowW && lowH && lowFormat !== 0xffffffff
            ? length(lowW, lowH, lowFormat)
            : 0);
    let sheet;
    if (minor >= 3)
        for (let i = 0; i < r.u32(68); i++) {
            const o = 80 + i * 8;
            if (r.u8(o) === 0x10 && r.u8(o + 1) === 0 && r.u8(o + 2) === 0 && !(r.u8(o + 3) & 2)) {
                const start = r.u32(o + 4), size = r.u32(start);
                sheet = parseParticleSheet(r.slice(start + 4, size));
            }
            if (r.u8(o) === 0x30 && r.u8(o + 1) === 0 && r.u8(o + 2) === 0)
                offset = r.u32(o + 4);
        }
    const frameFaces = Array.from({ length: animation ? frames : 1 }, () =>
        Array.from({ length: Math.min(faces, 6) }, () => []));
    let decodedBytes = 0;
    for (let mip = mips - 1; mip >= 0; mip--) {
        const w = Math.max(1, width >> mip),
            h = Math.max(1, height >> mip),
            size = length(w, h, format);
        r.check(offset, size * faces * frames);
        for (let frame = 0; frame < frameFaces.length; frame++)
        for (let face = 0; face < frameFaces[frame].length; face++) {
            const source = r.slice(offset + (frame * faces + face) * size, size),
                gpu = useS3tc && compressed(format);
            decodedBytes += gpu ? size : w * h * 4 * ([24, 25, 27, 28, 29].includes(format) ? 4 : 1);
            if (decodedBytes > 128 * 1024 * 1024) throw new Error("Texture exceeds decoded animation budget");
            frameFaces[frame][face].unshift({
                width: w,
                height: h,
                data: gpu ? source.slice() : pixels(source, w, h, format),
            });
        }
        offset += size * faces * frames;
    }
    log.debug("parse complete", { width, height, format, mips, faces, frames,
        decodedFrames: frameFaces.length, decodedBytes,
        compressed: useS3tc && compressed(format), durationMs: performance.now() - started });
    return {
        width,
        height,
        format,
        compressed: useS3tc && compressed(format),
        faces: frameFaces[0],
        ...(animation ? { frameFaces } : {}),
        frames,
        flags,
        ...(sheet ? { sheet } : {}),
    };
}
